import { useEffect, useState } from "react";
import { Search, Zap, Cpu, Star, Clock } from "lucide-react";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// Surge XT factory/3rd-party patch browser (falls back to the internal patch bank).
export default function PatchBrowser({ value, onChange, categories = [] }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [patches, setPatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [quick, setQuick] = useState({ favorites: [], recent: [], favorite_ids: [] });
  const loadQuick = () => api.get("/synth/patches/quick").then(({ data }) => setQuick(data)).catch(() => {});
  useEffect(() => { loadQuick(); window.addEventListener("synth-rendered", loadQuick); return () => window.removeEventListener("synth-rendered", loadQuick); }, []);
  const toggleFav = async (e, p) => {
    e.stopPropagation();
    try { await api.post("/synth/patches/favorite", { patch_id: p.id }); await loadQuick(); } catch { /* noop */ }
  };
  const isFav = (id) => quick.favorite_ids.includes(id);
  const Row = ({ p, compact }) => {
    const active = value?.id === p.id;
    return (
      <div className={`flex items-center gap-1 rounded-md border transition-colors ${active ? "bg-primary/15 text-primary border-primary/40" : "hover:bg-muted border-transparent"} ${compact ? "px-2 py-1 shrink-0" : "px-2 py-1.5 w-full"}`}>
        <button type="button" onClick={() => onChange(p)} className="flex items-center gap-2 text-xs flex-1 min-w-0 text-left" data-testid={`patch-${p.id.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
          {p.engine === "surge" ? <Zap size={11} className="text-amber-400 shrink-0" /> : <Cpu size={11} className="text-muted-foreground shrink-0" />}
          <span className={`truncate ${compact ? "max-w-[120px]" : "flex-1"}`}>{p.name}</span>
          {!compact && <span className="font-mono text-[9px] text-muted-foreground shrink-0">{p.category}</span>}
        </button>
        <button type="button" onClick={(e) => toggleFav(e, p)} className={`shrink-0 ${isFav(p.id) ? "text-amber-400" : "text-muted-foreground/40 hover:text-amber-400"}`} title={isFav(p.id) ? "Retirer des favoris" : "Ajouter aux favoris"} data-testid={`patch-fav-${p.id.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
          <Star size={11} fill={isFav(p.id) ? "currentColor" : "none"} />
        </button>
      </div>
    );
  };

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/synth/patches", { params: { q, category: cat === "all" ? "" : cat, limit: 300 } })
        .then(({ data }) => { setPatches(data.patches); setTotal(data.total); })
        .catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q, cat]);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 flex flex-col min-h-0" data-testid="patch-browser">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher un patch (pad, bass, pluck…)" className="w-full h-8 pl-8 pr-2 rounded-md bg-background border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary" data-testid="patch-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="h-8 w-36 text-xs bg-background border-border" data-testid="patch-category"><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-border max-h-72">
            <SelectItem value="all">Toutes catégories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="text-[10px] font-mono text-muted-foreground mb-2" data-testid="patch-count">{total} patch{total > 1 ? "es" : ""}{patches.length < total ? ` · ${patches.length} affichés` : ""}</div>
      {(quick.favorites.length > 0 || quick.recent.length > 0) && (
        <div className="mb-3 space-y-1.5" data-testid="patch-quick">
          {quick.favorites.length > 0 && (
            <div><div className="text-[9px] font-mono text-amber-400 mb-1 flex items-center gap-1"><Star size={9} fill="currentColor" /> FAVORIS</div>
              <div className="flex gap-1 overflow-x-auto pb-1" data-testid="patch-favorites">{quick.favorites.map((p) => <Row key={p.id} p={p} compact />)}</div></div>
          )}
          {quick.recent.length > 0 && (
            <div><div className="text-[9px] font-mono text-muted-foreground mb-1 flex items-center gap-1"><Clock size={9} /> RÉCENTS</div>
              <div className="flex gap-1 overflow-x-auto pb-1" data-testid="patch-recent">{quick.recent.map((p) => <Row key={p.id} p={p} compact />)}</div></div>
          )}
        </div>
      )}
      <div className="overflow-y-auto max-h-[380px] space-y-0.5 pr-1" data-testid="patch-list">
        {patches.map((p) => <Row key={p.id} p={p} />)}
        {patches.length === 0 && <div className="text-xs text-muted-foreground/70 py-6 text-center">Aucun patch ne correspond.</div>}
      </div>
    </div>
  );
}
